import { useEffect, useMemo, useState } from 'react';
import { CalendarHeart, Search, RotateCcw } from 'lucide-react';
import { supabase } from '../services/supabase';
import Confirm from '../components/ui/Confirm';
import { fmtMonth, fmtTermino, mesesDelTermino } from '../lib/terminoSuscripcion';

// PR-J4b — Panel admin de suscripciones. Solo lectura + acción de reembolso
// manual (refund_subscription). El alta y la activación las hacen el webhook y
// el alumno; el admin no crea ni edita suscripciones a mano.
//
// LA COLUMNA «MES» Y SU FILTRO DABAN POR HECHO QUE UNA SUSCRIPCIÓN ERA DE UN MES.
//   Con la mig 315 cubre `term_months` meses. Un trimestre de octubre se pintaba
//   `10/2026` y, peor, el filtro «12/2026» NO lo encontraba: la pregunta que se
//   le hace a este filtro es «quién está dentro ese mes», y en diciembre esos
//   siete siguen ocupando su plaza. Ahora la columna muestra lo que cubre
//   (`10–12/2026`) y el filtro casa con CUALQUIER mes del término
//   (`mesesDelTermino`), que además alimenta las opciones del desplegable.

const STATUS_LABELS: Record<string, string> = {
  purchased: 'Comprada',
  activated: 'Activada',
  expired: 'Caducada',
  refunded: 'Reembolsada',
};
const STATUS_BADGE: Record<string, string> = {
  purchased: 'badge-silver',
  activated: 'badge-success',
  expired: 'badge-warning',
  refunded: 'badge-error',
};
// Categoría por edad. Duplicada en GruposPage y en los dos mapas de la app;
// el CHECK de la BD (migs 117 y 192) es la autoridad. Lo vigila
// `supabase/__tests__/categorias-grupo.test.ts`.
const CATEGORY_LABELS: Record<string, string> = {
  querubin: 'Querubín',
  prebenjamin: 'Prebenjamín',
  benjamin: 'Benjamín',
  alevin: 'Alevín',
  infantil: 'Infantil',
  cadete: 'Cadete',
  juvenil: 'Juvenil',
};
const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

interface SubRow {
  id: string;
  status: string;
  period_month: string | null;
  // mig 315: meses que cubre el término. 1 = mensual, 3 = trimestre.
  term_months: number | null;
  category: string | null;
  day_of_week: number | null;
  time_start: string | null;
  created_at: string;
  waitlisted_sessions_count: number;
  user_name: string;
  user_email: string;
  group_name: string | null;
  enrolled: number;
  waitlisted: number;
  // Bundle GYM+Tecnificación (mig 135): bundle_seq>0 marca un token de bundle;
  // todos los tokens del mismo pago comparten stripe_payment_intent_id.
  payment_intent: string | null;
  is_bundle: boolean;
}

// Shape del embed que devuelve supabase (tipado laxo como en el resto de páginas).
interface RawSub {
  id: string;
  status: string;
  period_month: string | null;
  term_months: number | null;
  category: string | null;
  day_of_week: number | null;
  time_start: string | null;
  created_at: string;
  waitlisted_sessions_count: number;
  stripe_payment_intent_id: string | null;
  bundle_seq: number | null;
  profiles: { full_name: string | null; email: string | null } | null;
  groups: { name: string } | null;
  enrollments: { status: string }[] | null;
}

export default function SuscripcionesPage() {
  const [subs, setSubs] = useState<SubRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fStatus, setFStatus] = useState('todos');
  const [fCategory, setFCategory] = useState('todas');
  const [fMonth, setFMonth] = useState('');
  const [confirmRefund, setConfirmRefund] = useState<SubRow | null>(null);
  const [confirmBundle, setConfirmBundle] = useState<SubRow | null>(null);
  const [actionMsg, setActionMsg] = useState('');
  // La consulta lleva tres embeds (`profiles!user_id`, `groups!group_id`,
  // `enrollments!subscription_id`). Un 300 PGRST201 en cualquiera de ellos
  // dejaba `data` en null y la página en "No hay suscripciones que coincidan
  // con los filtros": lo mismo que tiró /pedidos en el #496, y aquí además
  // manda a buscar el error en un filtro.
  const [errorCarga, setErrorCarga] = useState('');

  useEffect(() => {
    loadSubs();
  }, []);

  async function loadSubs() {
    setLoading(true);
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select(
        `id, status, period_month, term_months, category, day_of_week, time_start, created_at,
         waitlisted_sessions_count, stripe_payment_intent_id, bundle_seq,
         profiles!user_id(full_name, email),
         groups!group_id(name),
         enrollments!subscription_id(status)`
      )
      .order('created_at', { ascending: false });

    setErrorCarga(error ? `No se pudieron cargar las suscripciones: ${error.message}` : '');
    const rows = (data ?? []) as unknown as RawSub[];
    setSubs(
      rows.map<SubRow>((r) => {
        const enrs = r.enrollments ?? [];
        return {
          id: r.id,
          status: r.status,
          period_month: r.period_month,
          term_months: r.term_months,
          category: r.category,
          day_of_week: r.day_of_week,
          time_start: r.time_start,
          created_at: r.created_at,
          waitlisted_sessions_count: r.waitlisted_sessions_count,
          user_name: r.profiles?.full_name ?? '—',
          user_email: r.profiles?.email ?? '',
          group_name: r.groups?.name ?? null,
          enrolled: enrs.filter((e) => e.status === 'confirmed').length,
          waitlisted: enrs.filter((e) => e.status === 'waitlist').length,
          payment_intent: r.stripe_payment_intent_id,
          is_bundle: (r.bundle_seq ?? 0) > 0,
        };
      })
    );
    setLoading(false);
  }

  async function doRefund(sub: SubRow) {
    setActionMsg('');
    const { data, error } = await supabase.rpc('refund_subscription', {
      p_subscription_id: sub.id,
      p_reason: 'Reembolso manual desde panel admin',
    });
    const res = data as { ok?: boolean; cancelled?: number; error?: string } | null;
    if (error || !res?.ok) {
      setActionMsg(`Error al reembolsar: ${error?.message ?? res?.error ?? 'desconocido'}`);
    } else {
      setActionMsg(
        `Suscripción de ${sub.user_name} reembolsada. ${res.cancelled ?? 0} sesión(es) liberada(s).`
      );
    }
    setConfirmRefund(null);
    await loadSubs();
  }

  // Nº de tokens de suscripción del mismo pago (para el aviso "N suscripciones").
  function bundleTokenCount(sub: SubRow): number {
    if (!sub.payment_intent) return 1;
    return subs.filter((s) => s.payment_intent === sub.payment_intent).length;
  }

  // Cancelar bundle completo: revoca las N suscripciones + el crédito GYM del
  // mismo pago (refund_bundle). El reembolso del DINERO es manual.
  async function doRefundBundle(sub: SubRow) {
    setActionMsg('');
    if (!sub.payment_intent) {
      setActionMsg('Esta suscripción no tiene pago asociado; no se puede cancelar como bundle.');
      setConfirmBundle(null);
      return;
    }
    const { data, error } = await supabase.rpc('refund_bundle', {
      p_payment_intent_id: sub.payment_intent,
      p_reason: 'Cancelación de bundle desde panel admin',
    });
    const res = data as {
      ok?: boolean;
      subscriptions_refunded?: number;
      gym_credits_revoked?: number;
      error?: string;
    } | null;
    if (error || !res?.ok) {
      setActionMsg(`Error al cancelar el bundle: ${error?.message ?? res?.error ?? 'desconocido'}`);
    } else {
      setActionMsg(
        `Bundle de ${sub.user_name} cancelado: ${res.subscriptions_refunded ?? 0} suscripción(es) y ` +
          `${res.gym_credits_revoked ?? 0} acceso(s) Preparación Física revocados. Recuerda hacer el reembolso del ` +
          `dinero a mano por transferencia.`
      );
    }
    setConfirmBundle(null);
    await loadSubs();
  }

  // Meses presentes en los datos (para el filtro). Un trimestre aporta sus TRES:
  // si solo se listaran los `period_month`, diciembre podría no estar en el
  // desplegable aunque haya siete jugadores ocupando plaza en diciembre.
  const months = useMemo(
    () =>
      Array.from(new Set(subs.flatMap((s) => mesesDelTermino(s.period_month, s.term_months))))
        .sort()
        .reverse(),
    [subs]
  );

  const filtered = useMemo(
    () =>
      subs.filter((s) => {
        if (fStatus !== 'todos' && s.status !== fStatus) return false;
        if (fCategory !== 'todas' && s.category !== fCategory) return false;
        if (fMonth && !mesesDelTermino(s.period_month, s.term_months).includes(fMonth))
          return false;
        const q = search.toLowerCase();
        if (q && !s.user_name.toLowerCase().includes(q) && !s.user_email.toLowerCase().includes(q))
          return false;
        return true;
      }),
    [subs, fStatus, fCategory, fMonth, search]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <CalendarHeart size={20} className="text-gold-primary" />
        <h1 className="font-oswald text-2xl uppercase tracking-wider text-white">
          Suscripciones ({subs.length})
        </h1>
      </div>

      {actionMsg && (
        <div className="card text-sm text-white" data-testid="sub-action-msg">
          {actionMsg}
        </div>
      )}

      {/* Búsqueda + filtros */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search size={16} className="text-text-secondary" />
          <input
            type="text"
            placeholder="Buscar por alumno o email..."
            className="input py-2 px-4 flex-1"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="input py-1.5 text-sm w-auto"
          data-testid="filter-status"
          value={fStatus}
          onChange={(e) => setFStatus(e.target.value)}
        >
          <option value="todos">Todos los estados</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select
          className="input py-1.5 text-sm w-auto"
          value={fCategory}
          onChange={(e) => setFCategory(e.target.value)}
        >
          <option value="todas">Todas las categorías</option>
          {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select
          className="input py-1.5 text-sm w-auto"
          value={fMonth}
          onChange={(e) => setFMonth(e.target.value)}
        >
          <option value="">Todos los meses</option>
          {months.map((m) => (
            <option key={m} value={m}>
              {fmtMonth(m)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center h-48 items-center">
          <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
        </div>
      ) : errorCarga ? (
        <div className="card text-center py-12" data-testid="suscripciones-ilegibles">
          <p className="text-error text-sm">{errorCarga}</p>
          <p className="text-text-secondary text-sm mt-2">
            No es que no haya ninguna ni que el filtro no encaje: la consulta ha fallado. No des por
            hecho que nadie tiene suscripción.
          </p>
          <button
            className="btn-secondary mt-4"
            onClick={loadSubs}
            data-testid="suscripciones-reintentar"
          >
            Reintentar
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-12 text-text-secondary">
          <p>No hay suscripciones que coincidan con los filtros.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-bg-border">
                <th className="text-left py-3 px-4 font-oswald uppercase text-text-secondary">
                  Alumno
                </th>
                <th className="text-left py-3 px-4 font-oswald uppercase text-text-secondary">
                  Estado
                </th>
                <th className="text-left py-3 px-4 font-oswald uppercase text-text-secondary">
                  Meses
                </th>
                <th className="text-left py-3 px-4 font-oswald uppercase text-text-secondary">
                  Categoría
                </th>
                <th className="text-left py-3 px-4 font-oswald uppercase text-text-secondary">
                  Grupo
                </th>
                <th className="text-center py-3 px-4 font-oswald uppercase text-text-secondary">
                  Horario
                </th>
                <th className="text-center py-3 px-4 font-oswald uppercase text-text-secondary">
                  Inscritas
                </th>
                <th className="text-center py-3 px-4 font-oswald uppercase text-text-secondary">
                  Espera
                </th>
                <th className="text-center py-3 px-4 font-oswald uppercase text-text-secondary">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bg-border">
              {filtered.map((s) => (
                <tr
                  key={s.id}
                  className="hover:bg-bg-elevated/50 transition-colors"
                  data-testid={`sub-row-${s.id}`}
                >
                  <td className="py-3 px-4">
                    <div className="text-white">{s.user_name}</div>
                    <div className="text-text-secondary text-xs">{s.user_email}</div>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={STATUS_BADGE[s.status] ?? 'badge-silver'}
                      data-testid={`sub-status-${s.id}`}
                    >
                      {STATUS_LABELS[s.status] ?? s.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-text-secondary">
                    {fmtTermino(s.period_month, s.term_months)}
                  </td>
                  <td className="py-3 px-4 text-text-secondary">
                    {s.category ? (CATEGORY_LABELS[s.category] ?? s.category) : '—'}
                  </td>
                  <td className="py-3 px-4 text-text-secondary">{s.group_name ?? '—'}</td>
                  <td className="py-3 px-4 text-center text-text-secondary text-xs">
                    {s.day_of_week != null && s.time_start
                      ? `${DAYS[s.day_of_week]} ${s.time_start.slice(0, 5)}`
                      : '—'}
                  </td>
                  <td className="py-3 px-4 text-center font-oswald text-gold-primary">
                    {s.enrolled}
                  </td>
                  <td className="py-3 px-4 text-center font-oswald text-warning">
                    {s.waitlisted > 0 ? s.waitlisted : '—'}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {(s.status === 'activated' || s.status === 'purchased') &&
                      (s.is_bundle ? (
                        <button
                          onClick={() => setConfirmBundle(s)}
                          className="text-text-secondary hover:text-error transition-colors inline-flex items-center gap-1"
                          title="Cancelar bundle Preparación Física + Tecnificación"
                          data-testid={`sub-cancel-bundle-${s.id}`}
                        >
                          <RotateCcw size={14} /> Cancelar bundle
                        </button>
                      ) : (
                        <button
                          onClick={() => setConfirmRefund(s)}
                          className="text-text-secondary hover:text-error transition-colors inline-flex items-center gap-1"
                          title="Reembolsar suscripción"
                          data-testid={`sub-refund-${s.id}`}
                        >
                          <RotateCcw size={14} /> Reembolsar
                        </button>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Confirm
        open={!!confirmRefund}
        onClose={() => setConfirmRefund(null)}
        onConfirm={() => confirmRefund && doRefund(confirmRefund)}
        title="Reembolsar suscripción"
        message={
          confirmRefund
            ? `¿Reembolsar la suscripción de ${confirmRefund.user_name}? Se cancelarán sus inscripciones futuras y se liberarán las plazas. El reembolso del dinero se hace aparte en Stripe.`
            : ''
        }
        danger
      />

      <Confirm
        open={!!confirmBundle}
        onClose={() => setConfirmBundle(null)}
        onConfirm={() => confirmBundle && doRefundBundle(confirmBundle)}
        title="Cancelar bundle Preparación Física + Tecnificación"
        confirmLabel="Sí, cancelar bundle"
        message={
          confirmBundle
            ? `¿Cancelar el bundle de ${confirmBundle.user_name}? Se revocarán las ` +
              `${bundleTokenCount(confirmBundle)} suscripción(es) del bundle (cancelando inscripciones ` +
              `futuras) y el acceso Preparación Física asociado. ⚠ El reembolso del DINERO NO es automático: hazlo ` +
              `a mano por transferencia/Stripe.`
            : ''
        }
        danger
      />
    </div>
  );
}
